import type { RouteLocationNamedRaw, RouteLocationNormalizedLoaded, RouteLocationRaw } from 'vue-router'
import { classifyCareSidebarSubBucket } from '@/api/hmCareOrderTypes'

export type CareSubKey =
  | 'hm_orders'
  | 'pend_sched'
  | 'pend_resched'
  | 'fu_pend_sched'
  | 'fu_pend_resched'

const SUB_ROUTE_NAMES: Record<CareSubKey, string> = {
  hm_orders: 'Home',
  pend_sched: 'HmCareOrdersPendingSchedule',
  pend_resched: 'HmCareOrdersPendingReschedule',
  fu_pend_sched: 'HmCareOrdersFuPendingSchedule',
  fu_pend_resched: 'HmCareOrdersFuPendingReschedule',
}

const ROUTE_NAME_TO_SUB: Partial<Record<string, CareSubKey>> = {
  Home: 'hm_orders',
  HmCareOrdersPendingSchedule: 'pend_sched',
  HmCareOrdersPendingReschedule: 'pend_resched',
  HmCareOrdersFuPendingSchedule: 'fu_pend_sched',
  HmCareOrdersFuPendingReschedule: 'fu_pend_resched',
}

export const CARE_ORDERS_OVERLAY_ROUTE_NAMES = [
  'HmCareOrdersOrderDetail',
  'HmCareOrdersNew',
  'HmCareOrdersSchedule',
  'HmCareOrdersReschedule',
] as const

export type CareOrdersOverlayRouteName = (typeof CARE_ORDERS_OVERLAY_ROUTE_NAMES)[number]

export function careSubKeyFromRoute(
  route: Pick<RouteLocationNormalizedLoaded, 'name'>,
): CareSubKey | null {
  const name = route.name ? String(route.name) : ''
  return ROUTE_NAME_TO_SUB[name] ?? null
}

export function isCareOrdersOverlayListRoute(
  route: Pick<RouteLocationNormalizedLoaded, 'name'>,
): boolean {
  const name = route.name ? String(route.name) : ''
  return (CARE_ORDERS_OVERLAY_ROUTE_NAMES as readonly string[]).includes(name)
}

export function careOrdersListRouteForSub(key: CareSubKey): RouteLocationNamedRaw {
  return { name: SUB_ROUTE_NAMES[key] }
}

/** Service Status 筛选项 → 应对应的列表子页；无专属子页则 hm_orders */
export function careSubKeyForServiceStatusFilter(statusValue: string): CareSubKey {
  if (!statusValue || statusValue === 'all') return 'hm_orders'
  const bucket = classifyCareSidebarSubBucket(statusValue)
  return bucket ?? 'hm_orders'
}

export function careOrdersNewRoute(): RouteLocationNamedRaw {
  return { name: 'HmCareOrdersNew' }
}

export function careOrdersScheduleRoute(orderId: string, lineIndex = 0): RouteLocationNamedRaw {
  return {
    name: 'HmCareOrdersSchedule',
    params: { orderId: orderId.trim() },
    query: { lineIndex: String(lineIndex) },
  }
}

export function careOrdersRescheduleRoute(orderId: string, lineIndex = 0): RouteLocationNamedRaw {
  return {
    name: 'HmCareOrdersReschedule',
    params: { orderId: orderId.trim() },
    query: { lineIndex: String(lineIndex) },
  }
}

export function careOrdersDetailRoute(orderId: string): RouteLocationRaw {
  return {
    name: 'HmCareOrdersOrderDetail',
    params: { orderId: orderId.trim() },
  }
}

export function isCareOrdersDetailRoute(
  route: Pick<RouteLocationNormalizedLoaded, 'name'>,
): boolean {
  return route.name === 'HmCareOrdersOrderDetail'
}

export function isCareOrdersNewRoute(
  route: Pick<RouteLocationNormalizedLoaded, 'name'>,
): boolean {
  return route.name === 'HmCareOrdersNew'
}

export function isCareOrdersScheduleRoute(
  route: Pick<RouteLocationNormalizedLoaded, 'name'>,
): boolean {
  return route.name === 'HmCareOrdersSchedule'
}

export function isCareOrdersRescheduleRoute(
  route: Pick<RouteLocationNormalizedLoaded, 'name'>,
): boolean {
  return route.name === 'HmCareOrdersReschedule'
}

export function isCareOrdersScheduleOverlayRoute(
  route: Pick<RouteLocationNormalizedLoaded, 'name'>,
): boolean {
  return isCareOrdersScheduleRoute(route) || isCareOrdersRescheduleRoute(route)
}
